// =====================================================================================
// INVRT Radar — web console — account — the one-time credential card
//
// Three routes hand back a credential exactly once and never again: POST /keys and
// POST /keys/:id/rotate (the plaintext API key, contract §2), POST /webhooks and
// POST /webhooks/:id/rotate-secret (the signing secret, §7). The server stores only a
// hash (keys) or never lets the column be read (webhook secrets), so once this card is
// dismissed the value is gone for good. The card says so in as many words, offers a copy
// control, and is the ONLY place the value is ever rendered: the list below it is re-read
// from the server and never carries it.
// =====================================================================================

import { useState } from "react";
import { Check, Copy, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface OneTimeSecretCardProps {
  /** The card's heading, e.g. "Copy your key now". Doubles as its accessible region name. */
  title: string;
  /** The word the contract sentence names: "This is the only time the key is shown." */
  noun: "key" | "secret";
  /** The value itself. Lives in this component's props and nowhere else. */
  secret: string;
  /** What the value belongs to: the key's label, the endpoint's URL. */
  subject?: string;
  onDone: () => void;
}

export function OneTimeSecretCard({ title, noun, secret, subject, onDone }: OneTimeSecretCardProps) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // A browser with the clipboard blocked: the value is still on screen to select.
      setCopyFailed(true);
    }
  }

  return (
    <Card role="region" aria-label={title} className="border-primary-200 bg-primary-50/40">
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" aria-hidden="true" />
          <CardTitle>{title}</CardTitle>
        </div>
        {subject ? <p className="text-sm text-ink-muted">{subject}</p> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm font-medium text-ink">This is the only time the {noun} is shown.</p>
        <p className="text-sm text-ink-muted">
          Store it somewhere safe before you close this card. Radar keeps no copy it can
          show you again; if it is lost, rotate it and use the replacement.
        </p>
        <code className="block break-all rounded-lg border border-line bg-surface px-3 py-2 font-mono text-sm text-ink">
          {secret}
        </code>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void copy()}>
            {copied ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Done
          </Button>
          {copyFailed ? (
            <p className="text-xs text-danger">
              Copying was blocked by your browser. Select the value and copy it by hand.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
